import { useMemo, useState } from "react";
import { Alert, Card, Flex, Typography } from "antd";
import {
  ArrowsClockwiseIcon,
  InfoIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useNotification, useTranslate } from "@refinedev/core";
import {
  Button,
  EmptyState,
  Popconfirm,
  Table,
  Tag,
  Tooltip,
  type TableColumn,
} from "@libertify/ui";
import { useSession } from "hooks/useSession";
import { useUserRoles } from "hooks/useUserRoles";
import { useWorkspace } from "hooks/useWorkspace";
import { selectUserCan } from "helpers/utils";
import type { ScimGroupMapping } from "@/types";
import {
  useScimGroupMappings,
  useSyncScimDirectory,
  useUpdateScimGroupMapping,
} from "../hooks/useScimGroupMappings";
import {
  getErrorDetail,
  getGroupName,
  isConfigured,
  toMappingEntries,
  toMappingRequest,
  type MappingEntry,
} from "../helpers/scimGroupMappings";
import { GroupMappingModal } from "./group-mappings/GroupMappingModal";

const { Title, Paragraph, Text } = Typography;

type MappingRow = {
  key: string;
  group: ScimGroupMapping;
  entry: MappingEntry;
  /** Position of the entry in toMappingEntries(group) */
  index: number;
};

type ModalState = { mode: "add" } | { mode: "edit"; row: MappingRow };

export const GroupMappingsTab = () => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { accesses } = useSession();
  const canUpdate = Boolean(
    selectUserCan(accesses, {
      subject: "Organization.ScimGroupMapping",
      action: "update",
      scope: "ORG",
    })
  );
  const canSync = Boolean(
    selectUserCan(accesses, {
      subject: "Organization.ScimDirectory",
      action: "update",
      scope: "ORG",
    })
  );

  const { data: groups = [], isLoading, isError } = useScimGroupMappings();
  const {
    roles,
    isLoading: isLoadingRoles,
    error: rolesError,
  } = useUserRoles();
  const { workspaces } = useWorkspace();
  const { mutateAsync: updateMapping, isPending: isSaving } =
    useUpdateScimGroupMapping();
  const { mutateAsync: syncDirectory, isPending: isSyncing } =
    useSyncScimDirectory();
  const [modal, setModal] = useState<ModalState | null>(null);

  const rows = useMemo<MappingRow[]>(
    () =>
      groups.filter(isConfigured).flatMap((group) =>
        toMappingEntries(group).map((entry, index) => ({
          key: `${group.directory_group_id}-${index}`,
          group,
          entry,
          index,
        }))
      ),
    [groups]
  );

  const saveEntries = async (
    group: ScimGroupMapping,
    entries: MappingEntry[]
  ) => {
    try {
      await updateMapping({
        groupId: group.directory_group_id,
        values: toMappingRequest(entries),
      });
      notify?.({
        type: "success",
        message: t("media.organizations.groupMappings.savedMessage"),
      });
      return true;
    } catch (error) {
      console.error("Failed to update group mapping", error);
      notify?.({
        type: "error",
        // A 422 carries the rejected role or workspace ids in its message
        message:
          getErrorDetail(error) ||
          t("media.organizations.groupMappings.errorMessage"),
      });
      return false;
    }
  };

  const handleSubmit = async (groupId: string, entry: MappingEntry) => {
    const group = groups.find((item) => item.directory_group_id === groupId);
    if (!group || !modal) {
      return;
    }
    const entries = toMappingEntries(group);
    const nextEntries =
      modal.mode === "edit"
        ? entries.map((item, index) =>
            index === modal.row.index ? entry : item
          )
        : [...entries, entry];
    if (await saveEntries(group, nextEntries)) {
      setModal(null);
    }
  };

  const handleRemove = async (row: MappingRow) => {
    await saveEntries(
      row.group,
      toMappingEntries(row.group).filter((_, index) => index !== row.index)
    );
  };

  const handleSync = async () => {
    try {
      await syncDirectory();
      notify?.({
        type: "success",
        message: t("media.organizations.groupMappings.syncStarted"),
      });
    } catch (error) {
      console.error("Failed to sync SCIM directory", error);
      notify?.({
        type: "error",
        message: t("media.organizations.groupMappings.syncError"),
      });
    }
  };

  const getRoleName = (roleId: string) =>
    roles.find((role) => role.id === roleId)?.name ?? roleId;
  const isCustomRole = (roleId: string) =>
    Boolean(roles.find((role) => role.id === roleId)?.organization_id);
  const getWorkspaceName = (workspaceId: string) =>
    workspaces.find((workspace) => workspace.id === workspaceId)?.name ??
    workspaceId;

  const columns: TableColumn<MappingRow>[] = [
    {
      title: t("media.organizations.groupMappings.columns.group"),
      key: "group",
      // The group name only shows on its first row
      render: (_, row) =>
        row.index === 0 ? (
          <Text strong>{getGroupName(row.group)}</Text>
        ) : null,
    },
    {
      title: t("media.organizations.groupMappings.columns.role"),
      key: "role",
      render: (_, { entry }) =>
        entry.roleId ? (
          <Flex align="center" gap={8} wrap>
            <Text>{getRoleName(entry.roleId)}</Text>
            {isCustomRole(entry.roleId) ? (
              <Tag size="s" disabled>
                {t("media.organizations.groupMappings.custom")}
              </Tag>
            ) : null}
            {entry.level === "workspace" ? (
              <Tooltip
                content={t(
                  "media.organizations.groupMappings.workspaceRoleHint"
                )}
              >
                <InfoIcon
                  tabIndex={0}
                  aria-label={t(
                    "media.organizations.groupMappings.workspaceRoleHint"
                  )}
                />
              </Tooltip>
            ) : null}
          </Flex>
        ) : (
          <Text type="secondary">
            {t("media.organizations.groupMappings.noRole")}
          </Text>
        ),
    },
    {
      title: t("media.organizations.groupMappings.columns.workspaces"),
      key: "workspaces",
      render: (_, { entry }) =>
        entry.level === "organization" ? (
          <Text type="secondary">
            {t("media.organizations.groupMappings.wholeOrganization")}
          </Text>
        ) : (
          <Flex gap={8} wrap>
            {entry.workspaceIds.map((workspaceId) => (
              <Tag key={workspaceId} size="s" disabled>
                {getWorkspaceName(workspaceId)}
              </Tag>
            ))}
          </Flex>
        ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      hidden: !canUpdate,
      render: (_, row) => (
        <Flex justify="flex-end" gap={8}>
          <Button
            variant="tertiary"
            size="s"
            leftIcon={<PencilSimpleIcon />}
            onClick={() => setModal({ mode: "edit", row })}
          >
            {t("media.organizations.groupMappings.edit")}
          </Button>
          <Popconfirm
            title={t("media.organizations.groupMappings.removeConfirm")}
            confirmText={t("media.organizations.groupMappings.remove")}
            cancelText={t("media.organizations.index.cancel")}
            danger
            onConfirm={() => handleRemove(row)}
          >
            <Button
              variant="tertiary"
              size="s"
              danger
              leftIcon={<TrashIcon />}
            >
              {t("media.organizations.groupMappings.remove")}
            </Button>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  const renderContent = () => {
    if (isError || rolesError) {
      return (
        <Alert
          type="error"
          showIcon
          message={t("media.organizations.groupMappings.loadError")}
        />
      );
    }
    if (!isLoading && groups.length === 0) {
      return (
        <EmptyState
          title={t("media.organizations.groupMappings.emptyTitle")}
          description={t("media.organizations.groupMappings.emptyDescription")}
        />
      );
    }
    return (
      <Table
        data={rows}
        columns={columns}
        rowKey="key"
        loading={isLoading || isLoadingRoles}
        pagination={false}
      />
    );
  };

  return (
    <Card>
      <Flex vertical gap={24}>
        <Flex justify="space-between" align="flex-start" gap={16} wrap>
          <Flex vertical gap={4}>
            <Title level={4} style={{ margin: 0 }}>
              {t("media.organizations.groupMappings.title")}
            </Title>
            <Paragraph type="secondary" style={{ margin: 0 }}>
              {t("media.organizations.groupMappings.description")}
            </Paragraph>
          </Flex>
          <Flex gap={8}>
            {canSync ? (
              <Button
                variant="outlined"
                size="m"
                loading={isSyncing}
                leftIcon={<ArrowsClockwiseIcon />}
                onClick={() => {
                  handleSync().catch((error) => {
                    console.error("Unexpected SCIM sync error", error);
                  });
                }}
              >
                {t("media.organizations.groupMappings.syncNow")}
              </Button>
            ) : null}
            {canUpdate && !rolesError && groups.length > 0 ? (
              <Button
                variant="outlined"
                size="m"
                leftIcon={<PlusIcon />}
                onClick={() => setModal({ mode: "add" })}
              >
                {t("media.organizations.groupMappings.addMapping")}
              </Button>
            ) : null}
          </Flex>
        </Flex>
        {renderContent()}
      </Flex>
      {modal ? (
        <GroupMappingModal
          groups={groups}
          initialGroupId={
            modal.mode === "edit"
              ? modal.row.group.directory_group_id
              : undefined
          }
          initialEntry={modal.mode === "edit" ? modal.row.entry : undefined}
          roles={roles}
          workspaces={workspaces}
          isSaving={isSaving}
          onClose={() => setModal(null)}
          onSubmit={(groupId, entry) => {
            handleSubmit(groupId, entry).catch((error) => {
              console.error("Unexpected group mapping error", error);
            });
          }}
        />
      ) : null}
    </Card>
  );
};

export default GroupMappingsTab;
